import React, { useEffect, useState, memo } from 'react';
import { motion } from 'framer-motion';
import { DownloadIcon, CheckIcon, KeyRoundIcon, ShieldCheckIcon, ReceiptIcon } from 'lucide-react';
const included = ['Unlimited call capture & AI notes', 'Borrower profiles & timelines', 'Instant search across every transcript', 'Source-backed answers from your history', 'Works with any dialer — no integrations', 'Delete your calls and data anytime'];
const pricingFaqs = [{
  question: 'Why do I bring my own OpenAI API key?',
  answer: "Privacy and control. Your transcripts are processed through your own OpenAI account — not ours. You see exactly what's sent, you set your own usage limits, and your conversation data never routes through a third-party middleman. It also keeps the subscription price honest: you pay OpenAI for exactly the AI you use, at cost."
}, {
  question: 'Roughly what does the API usage cost?',
  answer: 'For typical loan officer call volume, transcription and note generation usually amount to a few dollars per month — billed directly by OpenAI to your account, at their standard rates. Heavy callers may see more; you can set hard spending caps in your OpenAI dashboard.'
}, {
  question: 'How does per-user billing work?',
  answer: "It's a flat $25 per user per month. Each loan officer gets their own private workspace — their calls, their borrowers, their memory. Add or remove seats as your team changes."
}, {
  question: 'Can I cancel anytime?',
  answer: 'Yes. There are no contracts or lock-ins. Cancel whenever you like — and because you can export or delete your data at any time, your conversation history stays yours either way.'
}];
export function Pricing() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="relative overflow-hidden" data-id="element-378">
      {/* Giant background word for spatial identity */}
      <span aria-hidden="true" className="font-mono-brand pointer-events-none absolute -right-10 top-[30rem] rotate-90 text-[10rem] font-bold tracking-tighter text-slate-900/[0.04] lg:text-[14rem]" data-id="element-379">
        SIMPLE
      </span>

      {/* Hero */}
      <section className="texture-dots pt-32 pb-16 lg:pt-40" data-id="element-380">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-381">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="mx-auto max-w-2xl text-center" data-id="element-382">
            <p className="font-mono-brand text-xs uppercase tracking-[0.2em] text-teal-600" data-id="element-383">
              // pricing
            </p>
            <h1 className="font-display mt-4 text-4xl font-bold tracking-tight text-ink sm:text-5xl lg:text-6xl" data-id="element-384">
              One plan. Your key. Your data.
            </h1>
            <p className="mt-5 text-lg leading-8 text-slate-600" data-id="element-385">
              Recall is $25 per user per month — and your AI runs on your own
              OpenAI account, so your borrower conversations never leave your
              control.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Price card + BYO key */}
      <section className="pb-20 lg:pb-28" data-id="element-386">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-387">
          <div className="mx-auto grid max-w-4xl gap-6 lg:grid-cols-5" data-id="element-388">
            {/* Price card */}
            <motion.div initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5
          }} className="relative rounded-2xl border border-teal-600/30 bg-white p-8 shadow-sm lg:col-span-3" data-id="element-389">
              <span className="font-mono-brand absolute right-6 top-6 text-[10px] tracking-widest text-slate-400" data-id="element-390">
                PL.01
              </span>
              <p className="font-mono-brand text-xs uppercase tracking-[0.2em] text-teal-600" data-id="element-391">
                per_seat
              </p>
              <div className="mt-4 flex items-baseline gap-2" data-id="element-392">
                <span className="font-display text-6xl font-bold tracking-tight text-slate-900" data-id="element-393">
                  $25
                </span>
                <span className="font-mono-brand text-sm text-slate-500" data-id="element-394">
                  / user / month
                </span>
              </div>
              <p className="mt-3 text-sm leading-6 text-slate-600" data-id="element-395">
                Everything in Recall, for every loan officer on your team. No
                tiers, no usage gates, no surprises.
              </p>
              <ul className="mt-6 space-y-3" data-id="element-396">
                {included.map(item => <li key={item} className="flex items-start gap-3" data-id="element-397">
                    <CheckIcon className="mt-0.5 h-4 w-4 flex-none text-teal-600" aria-hidden="true" data-id="element-398" />
                    <span className="text-sm text-slate-700" data-id="element-399">{item}</span>
                  </li>)}
              </ul>
              <div className="mt-8" data-id="element-400">
                <a href="https://github.com/Nimbus-Cloud-Solutions/recall-ai-install/releases/latest/download/Recall-Setup.exe" className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-teal-600 px-6 py-3.5 text-base font-bold text-white hover:bg-teal-700 transition-colors sm:w-auto" data-id="element-401">
                  <DownloadIcon className="h-5 w-5" aria-hidden="true" data-id="element-402" />
                  Download for Windows
                  <span className="font-mono-brand ml-1 whitespace-nowrap rounded border border-white/30 bg-white/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white" data-id="element-403">
                    latest
                  </span>
                </a>
              </div>
            </motion.div>

            {/* BYO key card */}
            <motion.div initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="relative flex flex-col rounded-2xl border border-slate-200 bg-white p-8 shadow-sm lg:col-span-2" data-id="element-404">
              <span className="font-mono-brand absolute right-6 top-6 text-[10px] tracking-widest text-slate-400" data-id="element-405">
                PL.02
              </span>
              <span className="flex h-11 w-11 items-center justify-center rounded-lg border border-teal-600/30 bg-teal-50" data-id="element-406">
                <KeyRoundIcon className="h-5 w-5 text-teal-600" aria-hidden="true" data-id="element-407" />
              </span>
              <h2 className="font-display mt-4 text-lg font-bold text-slate-900" data-id="element-408">
                Bring your own OpenAI key
              </h2>
              <p className="mt-2 text-sm leading-6 text-slate-600" data-id="element-409">
                Recall connects to your own OpenAI account. Your transcripts are
                processed under your key — not ours — so your borrower
                conversations go through infrastructure you control.
              </p>
              <ul className="mt-5 space-y-3" data-id="element-410">
                <li className="flex items-start gap-3" data-id="element-411">
                  <ShieldCheckIcon className="mt-0.5 h-4 w-4 flex-none text-teal-600" aria-hidden="true" data-id="element-412" />
                  <span className="text-sm text-slate-700" data-id="element-413">
                    Your key, your data, your usage limits
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-414">
                  <ReceiptIcon className="mt-0.5 h-4 w-4 flex-none text-teal-600" aria-hidden="true" data-id="element-415" />
                  <span className="text-sm text-slate-700" data-id="element-416">
                    AI usage billed at cost by OpenAI — typically a few dollars
                    a month
                  </span>
                </li>
              </ul>
              <p className="font-mono-brand mt-auto pt-6 text-[11px] leading-5 text-slate-400" data-id="element-417">
                api_key required · set up once in settings · spending caps
                available in your OpenAI dashboard
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing FAQ */}
      <section className="border-t border-slate-100 texture-scanlines py-20 lg:py-28" data-id="element-418">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8" data-id="element-419">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.6
        }} className="text-center" data-id="element-420">
            <p className="font-mono-brand text-xs uppercase tracking-[0.2em] text-teal-600" data-id="element-421">
              // pricing_faq
            </p>
            <h2 className="font-display mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl" data-id="element-422">
              Questions about the plan
            </h2>
          </motion.div>

          <div className="mt-12 divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white shadow-sm" data-id="element-423">
            {pricingFaqs.map((faq, i) => {
            const isOpen = openIndex === i;
            return <div key={faq.question} data-id="element-424">
                  <h3 data-id="element-425">
                    <button type="button" onClick={() => setOpenIndex(isOpen ? null : i)} aria-expanded={isOpen} className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left text-base font-semibold text-slate-900 hover:bg-slate-50 transition-colors" data-id="element-426">
                      {faq.question}
                      <span className="font-mono-brand flex-none text-sm text-teal-600" aria-hidden="true" data-id="element-427">
                        {isOpen ? '[–]' : '[+]'}
                      </span>
                    </button>
                  </h3>
                  {isOpen && <p className="px-6 pb-5 text-sm leading-6 text-slate-600" data-id="element-428">
                      {faq.answer}
                    </p>}
                </div>;
          })}
          </div>
        </div>
      </section>
    </div>;
}